// Client for /api/auth/*. Same-origin, cookie-based; no tokens in JS, nothing in localStorage.
const b64uToBuf = (s: string): ArrayBuffer => {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4));
  const u = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i);
  return u.buffer;
};
const bufToB64u = (buf: ArrayBuffer): string => {
  const u = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) { super(message); }
}

export interface AuthStatus { mode: 'off' | 'session'; google: boolean; passkeys: boolean }
/** `uid`: opaque per-account id (hash of the passkey user handle) — never an e-mail or a DB row id. */
export interface Me { email: string; role: 'admin' | 'diagnosta'; fresh: boolean; passkeys: number; uid: string }
export interface AdminPasskey { id: string; name: string; createdAt: number; lastUsedAt: number | null }
export interface AdminUser {
  id: number; email: string; role: 'admin' | 'diagnosta'; status: 'active' | 'blocked';
  createdAt: number; lastLoginAt: number | null; passkeyCount: number; passkeys: AdminPasskey[];
}
export interface AuditEvent { id: number; ts: number; user: string; event: string; meta: Record<string, unknown> }

export async function api<T = any>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, {
    method, credentials: 'same-origin',
    headers: body !== undefined || method === 'POST' ? { 'content-type': 'application/json' } : undefined,
    body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
  });
  let j: any = {};
  try { j = await r.json(); } catch { /* empty body */ }
  if (!r.ok) throw new ApiError(r.status, j.error || `HTTP ${r.status}`, j.code);
  return j as T;
}

export const passkeysSupported = () => typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;

export const getStatus = () => api<AuthStatus>('GET', '/api/auth/status');
export const getMe = () => api<Me>('GET', '/api/auth/me');
export const logout = () => api('POST', '/api/auth/logout');
export const logoutAll = () => api('POST', '/api/auth/logout-all');

/** Session clock from the server. `expiresAt`/`serverNow` are epoch ms on the SERVER clock. */
export interface SessionClock { serverNow: number; expiresAt: number; absoluteAt: number; extendable: boolean }
export const getSessionClock = () => api<SessionClock>('GET', '/api/auth/session');
export const refreshSession = () => api<SessionClock>('POST', '/api/auth/session/refresh');

export interface PendingInvite { id: string; email: string; role: 'admin' | 'diagnosta'; createdBy: string; createdAt: number; expiresAt: number }

function toRequestOptions(o: any): PublicKeyCredentialRequestOptions {
  return {
    challenge: b64uToBuf(o.challenge), rpId: o.rpId, timeout: o.timeout, userVerification: 'required',
    allowCredentials: (o.allowCredentials || []).map((c: any) => ({ type: 'public-key', id: b64uToBuf(c.id) })),
  };
}

async function assertion(optionsUrl: string, verifyUrl: string) {
  const o = await api('POST', optionsUrl);
  const cred = (await navigator.credentials.get({ publicKey: toRequestOptions(o) })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Anulowano');
  const r = cred.response as AuthenticatorAssertionResponse;
  return api('POST', verifyUrl, {
    id: cred.id,
    clientDataJSON: bufToB64u(r.clientDataJSON),
    authenticatorData: bufToB64u(r.authenticatorData),
    signature: bufToB64u(r.signature),
    userHandle: r.userHandle ? bufToB64u(r.userHandle) : '',
  });
}

export const passkeyLogin = () => assertion('/api/auth/passkey/login/options', '/api/auth/passkey/login');
export const passkeyStepUp = () => assertion('/api/auth/passkey/stepup/options', '/api/auth/passkey/stepup');

async function creation(optionsUrl: string, optionsBody: unknown, registerUrl: string, extra: Record<string, unknown>) {
  const o = await api('POST', optionsUrl, optionsBody);
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: b64uToBuf(o.challenge), rp: o.rp,
      user: { id: b64uToBuf(o.user.id), name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams, authenticatorSelection: o.authenticatorSelection,
      attestation: o.attestation, timeout: o.timeout,
      excludeCredentials: (o.excludeCredentials || []).map((c: any) => ({ type: 'public-key', id: b64uToBuf(c.id) })),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('Anulowano');
  const r = cred.response as AuthenticatorAttestationResponse;
  return api('POST', registerUrl, {
    ...extra, id: cred.id, clientDataJSON: bufToB64u(r.clientDataJSON), attestationObject: bufToB64u(r.attestationObject),
  });
}

export const registerFromInvite = (t: string, name: string) =>
  creation('/api/auth/invite/options', { t }, '/api/auth/invite/register', { t, name });
export const registerFromSession = (name: string) =>
  creation('/api/auth/passkey/register/options', {}, '/api/auth/passkey/register', { name });

/** The person dismissed the Face ID / Touch ID prompt (or none matched) — not a real failure. */
export function isCancel(e: unknown): boolean {
  const n = (e as any)?.name;
  return n === 'NotAllowedError' || n === 'AbortError' || (e instanceof Error && e.message === 'Anulowano');
}

/** Human message for a WebAuthn/DOM error. */
export function explain(e: unknown): string {
  const n = (e as any)?.name;
  if (n === 'NotAllowedError') return 'Anulowano albo brak passkeya dla tej strony.';
  if (n === 'InvalidStateError') return 'To urządzenie ma już passkey do tej aplikacji.';
  if (e instanceof ApiError && e.status === 401) return 'Logowanie nie powiodło się. Spróbuj ponownie albo skontaktuj się z administratorem.';
  if (e instanceof ApiError) return e.message;
  return 'Coś poszło nie tak. Spróbuj ponownie.';
}

// ── 401 hook: any /api call (except /api/auth/*) answered with 401 → listener ──
const WRAPPED = '__wiscvAuthHook';
const listeners = new Set<() => void>();
export function onUnauthorized(fn: () => void): () => void {
  listeners.add(fn);
  if (typeof window !== 'undefined' && !(window.fetch as any)[WRAPPED]) {
    const orig = window.fetch.bind(window);
    const wrapped = async (input: RequestInfo | URL, init?: RequestInit) => {
      const res = await orig(input, init);
      if (res.status === 401) {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
        try {
          const p = new URL(url, window.location.origin);
          if (p.origin === window.location.origin && p.pathname.startsWith('/api/') && !p.pathname.startsWith('/api/auth/')) {
            listeners.forEach(l => l());
          }
        } catch { /* ignore */ }
      }
      return res;
    };
    (wrapped as any)[WRAPPED] = true;
    window.fetch = wrapped as typeof window.fetch;
  }
  return () => { listeners.delete(fn); };
}
